import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FileText,
  FileCheck,
  Users,
  GitPullRequest,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api().get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      msa_template_created: 'MSA template created',
      msa_document_created: 'MSA document created',
      msa_sent_for_signature: 'MSA sent for signature',
      po_template_created: 'PO template created',
      po_document_created: 'PO document created',
      vendor_invited: 'Vendor invited',
      change_request_created: 'Change request submitted',
      change_request_ai_reviewed: 'AI reviewed change request',
      change_request_approved: 'Change request approved',
      document_signed: 'Document signed',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  const getActionColor = (action) => {
    if (action.includes('approved') || action.includes('signed')) return 'bg-emerald-500';
    if (action.includes('rejected')) return 'bg-rose-500';
    if (action.includes('created') || action.includes('invited')) return 'bg-blue-500';
    return 'bg-amber-500';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-loading">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 skeleton rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
          Welcome back, {user?.full_name?.split(' ')[0]}
        </h1>
        <p className="text-zinc-400 mt-1">
          Here's an overview of your contract management activity
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card
          className="stat-card cursor-pointer hover:border-blue-500/50 transition-colors"
          onClick={() => navigate('/msa')}
          data-testid="stat-total-msas"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-white">{stats?.total_msas || 0}</p>
                <p className="stat-label">Total MSAs</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            {stats?.pending_msas > 0 && (
              <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
                <Clock className="w-4 h-4" />
                {stats.pending_msas} pending action
              </div>
            )}
          </CardContent>
        </Card>

        <Card
          className="stat-card cursor-pointer hover:border-emerald-500/50 transition-colors"
          onClick={() => navigate('/po')}
          data-testid="stat-total-pos"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-white">{stats?.total_pos || 0}</p>
                <p className="stat-label">Total POs</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            {stats?.pending_pos > 0 && (
              <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
                <Clock className="w-4 h-4" />
                {stats.pending_pos} pending action
              </div>
            )}
          </CardContent>
        </Card>

        <Card
          className="stat-card cursor-pointer hover:border-violet-500/50 transition-colors"
          onClick={() => navigate('/vendors')}
          data-testid="stat-total-vendors"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-white">{stats?.total_vendors || 0}</p>
                <p className="stat-label">Vendors</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="stat-card cursor-pointer hover:border-amber-500/50 transition-colors"
          onClick={() => navigate('/change-requests')}
          data-testid="stat-change-requests"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-white">
                  {stats?.pending_change_requests || 0}
                </p>
                <p className="stat-label">Pending Changes</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <GitPullRequest className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            {stats?.pending_change_requests > 0 && (
              <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                Requires review
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="lg:col-span-1 bg-zinc-900/40 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg text-zinc-200 font-['Manrope']">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full justify-between bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-0"
              onClick={() => navigate('/msa?action=new-template')}
              data-testid="quick-action-msa"
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Create MSA Template
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              className="w-full justify-between bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-0"
              onClick={() => navigate('/po?action=new-template')}
              data-testid="quick-action-po"
            >
              <span className="flex items-center gap-2">
                <FileCheck className="w-4 h-4" />
                Create PO Template
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              className="w-full justify-between bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-0"
              onClick={() => navigate('/vendors?action=invite')}
              data-testid="quick-action-vendor"
            >
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Invite Vendor
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="lg:col-span-2 bg-zinc-900/40 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-zinc-200 font-['Manrope']">
              Recent Activity
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate('/audit')}
              data-testid="view-all-activity"
            >
              View all
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="space-y-1">
                {stats.recent_activity.slice(0, 6).map((activity) => (
                  <div
                    key={activity.id}
                    className="activity-item"
                    data-testid={`activity-${activity.id}`}
                  >
                    <div
                      className={`activity-dot ${getActionColor(activity.action)}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-300 truncate">
                        {getActionLabel(activity.action)}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {activity.user_email} • {formatTime(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <TrendingUp className="empty-state-icon" />
                <p className="text-zinc-400">No recent activity</p>
                <p className="text-zinc-500 text-sm">
                  Your activity will appear here
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg text-zinc-200 font-['Manrope'] flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              Signed Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-emerald-400 font-['Manrope']">
                {stats?.signed_msas || 0}
              </span>
              <span className="text-zinc-500">MSAs fully executed</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg text-zinc-200 font-['Manrope'] flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Awaiting Signature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-amber-400 font-['Manrope']">
                {(stats?.pending_msas || 0) + (stats?.pending_pos || 0)}
              </span>
              <span className="text-zinc-500">documents pending</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
